<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import type { FieldSchema } from '../../../types';
import { capitalizeStr } from '@Helpers';
import { GetAnimalTemplatesService, CATEGORY } from '@AnimalModule';

defineOptions({ inheritAttrs: false });

const props = defineProps<{
  fieldLoading?: boolean;
  field?: FieldSchema;
}>();

const model = defineModel<string>();

const animalTemplates = ref();

const specieOptionClasses = computed(() => [
  'grid place-content-center rounded-md h-9 border px-4 py-1.5 transition-colors',
  {
    'active:bg-brand-600 hover:bg-brand-500 hover:text-white cursor-pointer ': !props.fieldLoading,
  },
]);

onMounted(() => getAnimalTemplates());

function getSortedTemplates(result: any[]) {
  const priority = [CATEGORY.DOG, CATEGORY.CAT, CATEGORY.RABBIT, CATEGORY.HORSE];

  const getRank = (name: string) => {
    if (name === CATEGORY.OTHER) return 999;

    const index = priority.indexOf(name as CATEGORY);
    return index === -1 ? 100 : index;
  };

  return result
    .slice()
    .sort((a: any, b: any) => getRank(a.name) - getRank(b.name))
    .map((item: any) => ({
      name: capitalizeStr(item.label),
      value: item.name,
    }));
}

async function getAnimalTemplates() {
  if (props.field?.settings?.show === false) return;

  const options = props.field?.edit?.options;
  if (options?.length) {
    animalTemplates.value = getSortedTemplates(options);
    return;
  }

  GetAnimalTemplatesService().then((result: any) => {
    animalTemplates.value = getSortedTemplates(result);
  });
}
</script>

<template>
  <div class="flex flex-wrap gap-2">
    <div
      v-for="{ name, value } in animalTemplates"
      class="flex items-center"
      :key="`${name}_${value}`"
    >
      <label
        :class="[
          ...specieOptionClasses,
          {
            'af-button border bg-brand-500 font-semibold text-white': model === value,
          },
        ]"
        :for="`${name}_${value}`"
      >
        <RadioButton
          v-model="model"
          class="hidden"
          name="specie"
          :disabled="fieldLoading"
          :inputId="`${name}_${value}`"
          :value="value"
        />

        <label
          :class="['text-sm', { 'cursor-pointer': !fieldLoading }]"
          :for="`${name}_${value}`"
        >
          {{ $t(name) }}
        </label>
      </label>
    </div>
  </div>
</template>
